/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICheckoutViewProps, ILineItem, ILineItemDeliveryGroup, ILineItems, IOrderSummary, IPickUpAtStore } from './lds-checkout-4';
import { IEmailDelivery } from './components';
import { IInvoicePaymentSummary } from './components/get-invoice-payment-summary';

// @ts-ignore
import { Secondary } from '@churchofjesuschrist/eden-buttons';
//@ts-ignore
import { Error } from '@churchofjesuschrist/eden-alert';

import { getRetailChannelAsync } from '../../actions/DataActionExtension.g';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);

const OrderSummaryComponent: React.FC<IOrderSummary> = props => {
    const { heading, lines } = props;
    return (
        <div className='msc-order-summary-wrapper'>
            {heading}
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        {lines.subtotal}
                        {lines.shipping}
                        {lines.otherCharge}
                        {lines.tax}
                        {lines.orderTotal}
                    </>
                )}
            </div>
        </div>
    );
};

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const CheckoutView: React.FC<ICheckoutViewProps> = props => {
    const [disableCheckout, setDisableCheckout] = React.useState(0);

    const [paymentType, setPaymentType] = React.useState<any>();
    const {
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        placeOrderButton,
        termsAndConditions,
        checkoutExpressPaymentContainer,
        checkoutErrorRef
    } = props;
    React.useEffect(() => {
        const paymentType = sessionStorage.getItem('paymentTypeId');
        setPaymentType(paymentType);
    }, [props]);

    const getRetailChannel = async () => {
        const ctx = { callerContext: props.context.actionContext };

        // @ts-ignore
        const channel = await getRetailChannelAsync(ctx, props.context.request.channel?.RecordId);
        setDisableCheckout(channel.DisableCheckout);
    };

    React.useEffect(() => {
        void getRetailChannel();
    }, []);

    if (disableCheckout === 1) {
        return (
            <div style={{ height: '65vh' }}>
                <div style={{ paddingTop: '20px' }}>
                    <Error>
                        {/* @ts-ignore */}
                        <div style={{ paddingTop: '3px' }}>{props.resources.checkoutDisabledText}</div>
                    </Error>
                </div>
            </div>
        );
        // } else if (props.isPlaceOrderClicked && props.isPlaceOrderClicked === true) {
    } else if (disableCheckout === 1) {
        return (
            <div style={{ display: 'flex', width: '100%', justifyContent: 'space-around', marginTop: '20px', marginBottom: '20px' }}>
                <Waiting className='msc-waiting-circular msc-waiting-lg' />
            </div>
        );
    } else {
        return (
            <Module {...checkoutProps} ref={checkoutErrorRef}>
                {!hasSalesOrder && !checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                {!hasSalesOrder &&
                    isPaymentVerificationRedirection &&
                    shouldEnableSinglePaymentAuthorizationCheckout &&
                    !alert &&
                    !loading && <Waiting className='msc-waiting-circular msc-waiting-lg' />}
                {/* @ts-ignore */}
                <div style={{ width: '100%' }}>{alert && alert.props.message && <Error>{alert.props.message}</Error>}</div>

                {!hasSalesOrder && (
                    <Node {...bodyProps}>
                        {loading}
                        {/* {alert} */}
                        {canShow && (
                            <>
                                <Node
                                    {...mainProps}
                                    style={{
                                        filter: props.isPlaceOrderClicked && props.config.shouldBlurAfterOrderPlaced ? 'blur(4px)' : ''
                                    }}
                                >
                                    {checkoutExpressPaymentContainer}
                                    {checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                                    {guidedForm}
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    <Node {...mainControlProps}>
                                        {paymentType &&
                                            paymentType !== '7' &&
                                            paymentType !== '100' &&
                                            paymentType !== '102' &&
                                            paymentType !== '103' && (
                                                <Secondary
                                                    // @ts-ignore
                                                    onClick={() => placeOrderButton.props.placeOrder()}
                                                    // @ts-ignore
                                                    disabled={props.isPlaceOrderClicked ? true : !placeOrderButton.props.canPlaceOrder}
                                                    style={{ width: '100%' }}
                                                    // fullWidth={true}
                                                >
                                                    {props.isPlaceOrderClicked ? (
                                                        <div
                                                            style={{
                                                                width: '30px',
                                                                height: '30px',
                                                                display: 'inline-block',
                                                                position: 'relative',
                                                                zIndex: 1,
                                                                borderRadius: '50%',
                                                                borderColor: 'rgba(209,209,209,0.5)',
                                                                borderStyle: 'solid',
                                                                borderWidth: '3px',
                                                                borderTopColor: '#01b6d1',
                                                                transform: 'translate3d(-50%, -50%, 0)',
                                                                animation: 'spin .75s ease-in-out infinite'
                                                            }}
                                                        />
                                                    ) : (
                                                        props.resources.placeOrderText
                                                    )}
                                                </Secondary>
                                            )}
                                    </Node>
                                </Node>
                                <Node {...sideProps}>
                                    {!hasInvoiceLine
                                        ? orderSummary && <OrderSummaryComponent {...orderSummary} />
                                        : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                                    <Node {...sideControlSecondProps}>
                                        <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                        {paymentType &&
                                            paymentType !== '7' &&
                                            paymentType !== '100' &&
                                            paymentType !== '102' &&
                                            paymentType !== '103' && (
                                                <Secondary
                                                    // @ts-ignore
                                                    onClick={() => placeOrderButton.props.placeOrder()}
                                                    // @ts-ignore
                                                    disabled={!placeOrderButton.props.canPlaceOrder}
                                                    style={{ width: '100%' }}
                                                >
                                                    {props.resources.placeOrderText}
                                                </Secondary>
                                            )}
                                    </Node>
                                </Node>
                                {props.isPlaceOrderClicked && props.config.shouldBlurAfterOrderPlaced && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '0',
                                            left: '0',
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
                                                <Waiting className='msc-waiting-circular msc-waiting-lg' />
                                            </div>
                                            <h2>{props.resources.processingOrder}</h2>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </Node>
                )}
                {hasSalesOrder && orderConfirmation}
            </Module>
        );
    }
};

export default CheckoutView;
